/**
 * Centralized storage cleanup helpers.
 *
 * Why this exists:
 *  - In normal browsing sessions, stale `arkeonx_*` and `sb-*` (Supabase) keys
 *    pollute localStorage / sessionStorage and cause the app to behave
 *    differently than in incognito (where storage is empty).
 *  - signOut() does not always reach Supabase (network errors), so `sb-*`
 *    tokens may survive across logout, causing "zombie" sessions.
 *
 * This module provides a single source of truth for clearing app state.
 */

const ARK_PREFIX = "arkeonx_";
const SB_PREFIX = "sb-";

/** Keys we explicitly preserve across logout (e.g. user UI prefs). */
const PRESERVE_KEYS = new Set<string>([
  // none for now; add explicit user prefs here if needed
]);

const PRESERVE_PREFIXES: string[] = [
  "arkeonx_dismissed_report_notifs_",
];

const isArkeonxKey = (key: string) => key.startsWith(ARK_PREFIX);
const isSupabaseKey = (key: string) => key.startsWith(SB_PREFIX);

/** Remove all Arkeonx + Supabase keys from a Storage instance. */
const wipeStorage = (storage: Storage, opts: { includeSupabase: boolean }) => {
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (!key) continue;
      if (PRESERVE_KEYS.has(key) || PRESERVE_PREFIXES.some((p) => key.startsWith(p))) continue;
      if (isArkeonxKey(key) || (opts.includeSupabase && isSupabaseKey(key))) {
        toRemove.push(key);
      }
    }
    toRemove.forEach((k) => {
      try { storage.removeItem(k); } catch {}
    });
  } catch {}
};

/**
 * Full cleanup at signOut: drop Arkeonx state AND Supabase tokens.
 * Use this from AuthContext.signOut() and from boot when tokens are invalid.
 */
export const clearAllAuthStorage = () => {
  if (typeof window === "undefined") return;
  wipeStorage(window.localStorage, { includeSupabase: true });
  wipeStorage(window.sessionStorage, { includeSupabase: true });
};

/** Remove only Supabase auth tokens, preserving onboarding/upload state. */
export const clearSupabaseAuthStorage = () => {
  if (typeof window === "undefined") return;
  const wipeSupabaseOnly = (storage: Storage) => {
    try {
      const toRemove: string[] = [];
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (key && isSupabaseKey(key)) toRemove.push(key);
      }
      toRemove.forEach((key) => {
        try { storage.removeItem(key); } catch {}
      });
    } catch {}
  };
  wipeSupabaseOnly(window.localStorage);
  wipeSupabaseOnly(window.sessionStorage);
};

/**
 * Soft cleanup at boot: drop only Arkeonx legacy/cache keys, preserve
 * Supabase tokens (let the SDK validate them itself).
 */
export const clearArkeonxCaches = () => {
  if (typeof window === "undefined") return;
  wipeStorage(window.localStorage, { includeSupabase: false });
  wipeStorage(window.sessionStorage, { includeSupabase: false });
};
